import React, { useState, useEffect } from "react";
import { Modal, Spin } from "antd";
import { i18nUtil, getFileExtension } from "@aspen/libs";

interface IModalFilePreviewProps {
  previewFile: {
    url: string;
    title: string;
  } | null;
  onClose: () => void;
}

const ModalFilePreview: React.FC<IModalFilePreviewProps> = ({ previewFile, onClose }) => {
  const intl = i18nUtil.t();
  const [fileType, setFileType] = useState<string>("unknown");

  useEffect(() => {
    if (previewFile?.url) {
      const extension = getFileExtension(previewFile.url);
      if (!extension) {
        setFileType("unknown");
        return;
      }
      // 直接根据扩展名判断文件类型
      if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)) {
        setFileType("image");
      } else if (
        ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv", "mp3", "wav", "ogg"].includes(extension)
      ) {
        setFileType("video");
      } else if (extension === "pdf") {
        setFileType("pdf");
      } else if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"].includes(extension)) {
        setFileType("office");
      } else {
        setFileType("unknown");
      }
    }
  }, [previewFile]);

  const renderPreviewContent = () => {
    if (!previewFile) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Spin size="large" tip={intl["file.preview.loading"]} />
        </div>
      );
    }

    switch (fileType) {
      case "image":
        return (
          <div className="flex items-center justify-center min-h-[400px]">
            <img
              src={previewFile.url}
              alt={previewFile.title}
              className="max-w-full max-h-[70vh] object-contain bg-[#2f3342]"
            />
          </div>
        );

      case "video":
        return (
          <div className="flex items-center justify-center min-h-[400px]">
            <video
              src={previewFile.url}
              controls
              className="max-w-full max-h-[70vh]"
              title={previewFile.title}>
              <track kind="captions" src="" srcLang="en" label="English" />
            </video>
          </div>
        );

      case "pdf":
        return (
          <div className="w-full h-[70vh] min-h-[400px]">
            <iframe
              src={previewFile.url}
              className="w-full h-full border-0"
              title={previewFile.title}
            />
          </div>
        );

      case "office":
        return (
          <div className="w-full h-[70vh] min-h-[400px]">
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewFile.url)}`}
              className="w-full h-full border-0"
              title={previewFile.title}
            />
          </div>
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
            <div className="text-blue-500 text-lg font-medium mb-2">
              {intl["file.preview.unsupported.title"]}
            </div>
            <div className="text-gray-600">{intl["file.preview.unsupported.message"]}</div>
          </div>
        );
    }
  };

  return (
    <Modal
      title={previewFile?.title || ""}
      open={!!previewFile}
      onCancel={onClose}
      width={800}
      centered
      footer={null}>
      <div className="overflow-auto relative">{renderPreviewContent()}</div>
    </Modal>
  );
};

export { ModalFilePreview };
